import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Loader2, AlertTriangle, CheckCircle2, Image as ImageIcon } from 'lucide-react';
import { albumsApi } from '../../api/albums';
import { subscribeToAlbumStream } from '../../api/sse';
import { InfoTip } from '../UI/InfoTip';
import styles from './LiveAlbumStats.module.css';

interface LiveAlbumStatsProps {
  albumId: string;
  albumCreatedAt: string;
}

interface LiveStatus {
  hasOverlays: boolean;
  processedCount: number;
  pendingCount: number;
  failedCount: number;
  lastPhotoAddedAt: string | null;
  lastProcessedAt: string | null;
}

const POLL_INTERVAL_MS = 15_000;
const SSE_REFRESH_DEBOUNCE_MS = 800;

function timeAgo(iso: string | null): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const secs = Math.floor(diff / 1000);
  if (secs < 10) return 'just now';
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function LiveAlbumStats({ albumId, albumCreatedAt }: LiveAlbumStatsProps) {
  const [status, setStatus] = useState<LiveStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [, setNowTick] = useState(0);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchStatus = async () => {
    try {
      const data = await albumsApi.getLiveStatus(albumId);
      setStatus(data);
      setError(false);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [albumId]);

  useEffect(() => {
    return subscribeToAlbumStream(albumId, {
      onPhotoAdded: () => {
        if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = setTimeout(fetchStatus, SSE_REFRESH_DEBOUNCE_MS);
      },
    });
  }, [albumId]);

  useEffect(() => {
    const t = setInterval(() => setNowTick((n) => n + 1), 15_000);
    return () => clearInterval(t);
  }, []);

  if (loading || !status) {
    return (
      <div className={styles['stats-block']}>
        <div className={styles['header-row']}>
          <span className={styles['live-dot']} />
          <span className={styles['count-muted']}>Loading…</span>
        </div>
      </div>
    );
  }

  if (error) return null;

  const total = status.processedCount + status.pendingCount + status.failedCount;
  const indexed = status.processedCount;
  const pending = status.pendingCount;
  const failed = status.failedCount;
  const hasOverlays = status.hasOverlays;

  const percent = total > 0 ? Math.min(100, Math.round((indexed / total) * 100)) : 0;
  const isCaughtUp = total > 0 && pending === 0 && failed === 0;
  const isEmpty = total === 0;

  const lastIso = status.lastPhotoAddedAt ?? status.lastProcessedAt;
  const latestLabel = timeAgo(lastIso);
  const sinceLabel = timeAgo(albumCreatedAt);
  const showSince = !!lastIso && sinceLabel !== latestLabel;

  const blockClass = failed > 0
    ? `${styles['stats-block']} ${styles['tone-failed']}`
    : styles['stats-block'];

  return (
    <div className={blockClass} onClick={(e) => e.stopPropagation()}>
      {/* Row 1 — header: count on left, status indicator on right */}
      <div className={styles['header-row']}>
        <span className={styles['header-left']}>
          <span className={styles['live-dot']} aria-hidden />
          {isEmpty ? (
            <span className={styles['count-muted']}>Waiting for first photo…</span>
          ) : hasOverlays ? (
            <>
              <span className={styles['count-strong']}>{indexed.toLocaleString()}</span>
              <span className={styles['count-divider']}>/</span>
              <span className={styles['count-total']}>{total.toLocaleString()}</span>
              <span className={styles['count-label']}>processed</span>
              <InfoTip label="What does processed mean?">
                Photos composited with the album&apos;s overlay (frame, logo, branding) and delivered to guests. The original photo is replaced with the overlay-baked version on the album.
              </InfoTip>
            </>
          ) : (
            <>
              <span className={styles['count-strong']}>{total.toLocaleString()}</span>
              <span className={styles['count-label']}>photos synced</span>
            </>
          )}
        </span>

        {hasOverlays && !isEmpty && (
          <span className={styles['status-slot']}>
            {isCaughtUp ? (
              <span className={`${styles['status-chip']} ${styles['chip-ready']}`}>
                <CheckCircle2 className={styles['icon-xs']} aria-hidden />
                Caught up
              </span>
            ) : (
              <span className={styles['percent']}>{percent}%</span>
            )}
          </span>
        )}
      </div>

      {/* Row 2 — progress bar (only show if hasOverlays) */}
      {hasOverlays && (
        <div className={styles['progress-track']} aria-hidden>
          <motion.div
            className={styles['progress-fill']}
            initial={false}
            animate={{ width: isEmpty ? '0%' : `${percent}%` }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          />
        </div>
      )}

      {/* Row 3 — timeline meta + inline status pills */}
      <div className={styles['meta-row']}>
        <div className={styles['meta-left']}>
          {isEmpty ? (
            <span>Live for {sinceLabel}</span>
          ) : (
            <>
              <span className={styles['meta-primary']}>Latest {latestLabel}</span>
              {showSince && (
                <>
                  <span className={styles['meta-dot']} aria-hidden>·</span>
                  <span className={styles['meta-secondary']}>started {sinceLabel}</span>
                </>
              )}
            </>
          )}
        </div>

        <div className={styles['meta-right']}>
          {pending > 0 && (
            <span className={`${styles['inline-pill']} ${styles['pill-pending']}`}>
              <Loader2 className={`${styles['icon-xs']} ${styles['spin']}`} aria-hidden />
              {pending.toLocaleString()} {hasOverlays ? '' : 'syncing'}
            </span>
          )}
          {failed > 0 && (
            <span className={`${styles['inline-pill']} ${styles['pill-failed']}`}>
              <AlertTriangle className={styles['icon-xs']} aria-hidden />
              {failed.toLocaleString()}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
